import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  SalesByCategoryRow,
  SalesByDocTypeRow,
  SalesByPaymentMethodRow,
  SalesByPeriodRow,
  SalesByProductRow,
} from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import {
  fetchSalesByCategory,
  fetchSalesByDocType,
  fetchSalesByPaymentMethod,
  fetchSalesByProduct,
  fetchSalesTrend,
  reportKeys,
} from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import type { ReportFilterRow } from '@/features/reports/reportFilters';
import { usePortal } from '@/features/portal/PortalContext';

type Granularity = 'day' | 'month';

const money = (value: number) => value.toFixed(2);

// Each breakdown is its own table, so each keeps its own layout/search/
// filter state — a shared column picker across five differently-shaped
// tables would be ambiguous about which one it governed.

const TREND_FILTER_FIELDS = ['Period'] as const;
const TREND_TOGGLEABLE = ['orders', 'gross'] as const;
const TREND_LABELS: Record<string, string> = { orders: 'Orders', gross: 'Gross' };

const TREND_COLUMNS: Column<SalesByPeriodRow>[] = [
  { key: 'period', header: 'Period', pinned: true, cell: (row) => row.period },
  { key: 'orders', header: 'Orders', align: 'right', cell: (row) => String(row.orders) },
  { key: 'gross', header: 'Gross', align: 'right', cell: (row) => money(row.gross) },
];

const PRODUCT_FILTER_FIELDS = ['Product'] as const;
const PRODUCT_TOGGLEABLE = ['quantity', 'total'] as const;
const PRODUCT_LABELS: Record<string, string> = { quantity: 'Quantity', total: 'Total' };

const PRODUCT_COLUMNS: Column<SalesByProductRow>[] = [
  { key: 'productName', header: 'Product', pinned: true, cell: (row) => row.productName },
  { key: 'quantity', header: 'Quantity', align: 'right', cell: (row) => money(row.quantity) },
  { key: 'total', header: 'Total', align: 'right', cell: (row) => money(row.total) },
];

const CATEGORY_FILTER_FIELDS = ['Category'] as const;
const CATEGORY_TOGGLEABLE = ['quantity', 'total'] as const;
const CATEGORY_LABELS: Record<string, string> = { quantity: 'Quantity', total: 'Total' };

const CATEGORY_COLUMNS: Column<SalesByCategoryRow>[] = [
  { key: 'categoryName', header: 'Category', pinned: true, cell: (row) => row.categoryName },
  { key: 'quantity', header: 'Quantity', align: 'right', cell: (row) => money(row.quantity) },
  { key: 'total', header: 'Total', align: 'right', cell: (row) => money(row.total) },
];

const PAYMENT_METHOD_FILTER_FIELDS = ['Method'] as const;
const PAYMENT_METHOD_TOGGLEABLE = ['amount'] as const;
const PAYMENT_METHOD_LABELS: Record<string, string> = { amount: 'Amount' };

const PAYMENT_METHOD_COLUMNS: Column<SalesByPaymentMethodRow>[] = [
  { key: 'method', header: 'Method', pinned: true, cell: (row) => row.method },
  { key: 'amount', header: 'Amount', align: 'right', cell: (row) => money(row.amount) },
];

const DOC_TYPE_FILTER_FIELDS = ['Document type'] as const;
const DOC_TYPE_TOGGLEABLE = ['orders', 'gross'] as const;
const DOC_TYPE_LABELS: Record<string, string> = { orders: 'Orders', gross: 'Gross' };

const DOC_TYPE_COLUMNS: Column<SalesByDocTypeRow>[] = [
  { key: 'docType', header: 'Document type', pinned: true, cell: (row) => row.docType },
  { key: 'orders', header: 'Orders', align: 'right', cell: (row) => String(row.orders) },
  { key: 'gross', header: 'Gross', align: 'right', cell: (row) => money(row.gross) },
];

/** Sums the rows currently on screen — a client-side totals row, not a second round-trip. */
function sumTotals<T>(rows: readonly T[], keys: (keyof T & string)[]): Record<string, string> {
  const cells: Record<string, string> = {};
  for (const key of keys) {
    const sum = rows.reduce((acc, row) => acc + Number(row[key] ?? 0), 0);
    cells[key] = key === 'orders' ? String(sum) : sum.toFixed(2);
  }
  return cells;
}

/** One breakdown table's own search + Filters state, so five tables don't share one. */
function useTableFilters() {
  const [search, setSearch] = useState('');
  const [filterRows, setFilterRows] = useState<ReportFilterRow[]>([]);
  const [matchAny, setMatchAny] = useState(true);
  return {
    search,
    setSearch,
    filterRows,
    matchAny,
    applyFilters: (rows: ReportFilterRow[], nextMatchAny: boolean) => {
      setFilterRows(rows);
      setMatchAny(nextMatchAny);
    },
  };
}

export function SalesReportsPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);
  const [granularity, setGranularity] = useState<Granularity>('day');

  const trend = useTableFilters();
  const product = useTableFilters();
  const category = useTableFilters();
  const paymentMethod = useTableFilters();
  const docType = useTableFilters();

  const trendLayout = useReportLayout('sales-report-trend', TREND_TOGGLEABLE);
  const productLayout = useReportLayout('sales-report-product', PRODUCT_TOGGLEABLE);
  const categoryLayout = useReportLayout('sales-report-category', CATEGORY_TOGGLEABLE);
  const paymentMethodLayout = useReportLayout('sales-report-payment-method', PAYMENT_METHOD_TOGGLEABLE);
  const docTypeLayout = useReportLayout('sales-report-doc-type', DOC_TYPE_TOGGLEABLE);

  const trendColumns = applyReportLayout(TREND_COLUMNS, trendLayout);
  const productColumns = applyReportLayout(PRODUCT_COLUMNS, productLayout);
  const categoryColumns = applyReportLayout(CATEGORY_COLUMNS, categoryLayout);
  const paymentMethodColumns = applyReportLayout(PAYMENT_METHOD_COLUMNS, paymentMethodLayout);
  const docTypeColumns = applyReportLayout(DOC_TYPE_COLUMNS, docTypeLayout);

  const trendQuery = useQuery({
    queryKey: reportKeys.salesTrend(filters, granularity),
    queryFn: ({ signal }) => fetchSalesTrend(filters, granularity, signal),
  });
  const byProductQuery = useQuery({
    queryKey: reportKeys.salesByProduct(filters),
    queryFn: ({ signal }) => fetchSalesByProduct(filters, signal),
  });
  const byCategoryQuery = useQuery({
    queryKey: reportKeys.salesByCategory(filters),
    queryFn: ({ signal }) => fetchSalesByCategory(filters, signal),
  });
  const byPaymentMethodQuery = useQuery({
    queryKey: reportKeys.salesByPaymentMethod(filters),
    queryFn: ({ signal }) => fetchSalesByPaymentMethod(filters, signal),
  });
  const byDocTypeQuery = useQuery({
    queryKey: reportKeys.salesByDocType(filters),
    queryFn: ({ signal }) => fetchSalesByDocType(filters, signal),
  });

  return (
    <div>
      <PageHeader title="Sales reports" description="Trend and breakdowns for the selected window." />
      <ReportFilterBar
        filters={filters}
        onChange={setFilters}
        branches={profile?.branches ?? []}
        showDocType
        hideDateRange
      />
      <ReportToolbar>
        <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} />
      </ReportToolbar>

      <section className="mb-8">
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 className="text-sm font-semibold text-slate-900">Trend</h2>
          <div
            className="flex overflow-hidden rounded-lg border border-slate-300"
            role="group"
            aria-label="Trend granularity"
          >
            {(['day', 'month'] as Granularity[]).map((option, index) => (
              <button
                key={option}
                type="button"
                onClick={() => setGranularity(option)}
                aria-pressed={granularity === option}
                className={`px-3 py-2 text-sm font-medium capitalize transition-colors ${
                  index > 0 ? 'border-l border-slate-300' : ''
                } ${
                  granularity === option
                    ? 'bg-brand-600/[0.12] text-brand-700'
                    : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        <ReportToolbar>
          <ReportSearchField value={trend.search} onChange={trend.setSearch} placeholder="Search period" />
          <ReportFiltersButton
            fields={TREND_FILTER_FIELDS}
            applied={trend.filterRows}
            matchAny={trend.matchAny}
            onApply={trend.applyFilters}
            placeholderFor={() => 'period'}
          />
          <ReportColumnsButton layout={trendLayout} labels={TREND_LABELS} />
        </ReportToolbar>
        <AsyncSection
          state={trendQuery}
          select={(rows: SalesByPeriodRow[]) =>
            applyClientFilters(rows, {
              search: trend.search,
              filterRows: trend.filterRows,
              matchAny: trend.matchAny,
              fieldValue: (row, field) => (field === 'Period' ? row.period : undefined),
              searchValues: (row) => [row.period],
            })
          }
          isEmpty={(rows) => rows.length === 0}
          emptyTitle="No sales in this window"
          skeleton={<TableSkeleton columns={3} />}
        >
          {(rows) => (
            <DataTable
              caption="Sales trend"
              layout={trendLayout}
              columns={trendColumns}
              rows={rows}
              rowKey={(row) => row.period}
              totals={{ label: 'Total', cells: sumTotals(rows, ['orders', 'gross']) }}
            />
          )}
        </AsyncSection>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold text-slate-900">By product</h2>
        <ReportToolbar>
          <ReportSearchField value={product.search} onChange={product.setSearch} placeholder="Search product" />
          <ReportFiltersButton
            fields={PRODUCT_FILTER_FIELDS}
            applied={product.filterRows}
            matchAny={product.matchAny}
            onApply={product.applyFilters}
            placeholderFor={() => 'product'}
          />
          <ReportColumnsButton layout={productLayout} labels={PRODUCT_LABELS} />
        </ReportToolbar>
        <AsyncSection
          state={byProductQuery}
          select={(rows: SalesByProductRow[]) =>
            applyClientFilters(rows, {
              search: product.search,
              filterRows: product.filterRows,
              matchAny: product.matchAny,
              fieldValue: (row, field) => (field === 'Product' ? row.productName : undefined),
              searchValues: (row) => [row.productName],
            })
          }
          isEmpty={(rows) => rows.length === 0}
          emptyTitle="No product sales in this window"
          skeleton={<TableSkeleton columns={3} />}
        >
          {(rows) => (
            <DataTable
              caption="Sales by product"
              layout={productLayout}
              columns={productColumns}
              rows={rows}
              rowKey={(row, index) => `${row.productName}-${index}`}
              totals={{ label: 'Total', cells: sumTotals(rows, ['quantity', 'total']) }}
            />
          )}
        </AsyncSection>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold text-slate-900">By category</h2>
        <ReportToolbar>
          <ReportSearchField value={category.search} onChange={category.setSearch} placeholder="Search category" />
          <ReportFiltersButton
            fields={CATEGORY_FILTER_FIELDS}
            applied={category.filterRows}
            matchAny={category.matchAny}
            onApply={category.applyFilters}
            placeholderFor={() => 'category'}
          />
          <ReportColumnsButton layout={categoryLayout} labels={CATEGORY_LABELS} />
        </ReportToolbar>
        <AsyncSection
          state={byCategoryQuery}
          select={(rows: SalesByCategoryRow[]) =>
            applyClientFilters(rows, {
              search: category.search,
              filterRows: category.filterRows,
              matchAny: category.matchAny,
              fieldValue: (row, field) => (field === 'Category' ? row.categoryName : undefined),
              searchValues: (row) => [row.categoryName],
            })
          }
          isEmpty={(rows) => rows.length === 0}
          emptyTitle="No category sales in this window"
          skeleton={<TableSkeleton columns={3} />}
        >
          {(rows) => (
            <DataTable
              caption="Sales by category"
              layout={categoryLayout}
              columns={categoryColumns}
              rows={rows}
              rowKey={(row, index) => `${row.categoryName}-${index}`}
              totals={{ label: 'Total', cells: sumTotals(rows, ['quantity', 'total']) }}
            />
          )}
        </AsyncSection>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold text-slate-900">By payment method</h2>
        <ReportToolbar>
          <ReportSearchField
            value={paymentMethod.search}
            onChange={paymentMethod.setSearch}
            placeholder="Search method"
          />
          <ReportFiltersButton
            fields={PAYMENT_METHOD_FILTER_FIELDS}
            applied={paymentMethod.filterRows}
            matchAny={paymentMethod.matchAny}
            onApply={paymentMethod.applyFilters}
            placeholderFor={() => 'Cash / Card / Credit'}
          />
          <ReportColumnsButton layout={paymentMethodLayout} labels={PAYMENT_METHOD_LABELS} />
        </ReportToolbar>
        <AsyncSection
          state={byPaymentMethodQuery}
          select={(rows: SalesByPaymentMethodRow[]) =>
            applyClientFilters(rows, {
              search: paymentMethod.search,
              filterRows: paymentMethod.filterRows,
              matchAny: paymentMethod.matchAny,
              fieldValue: (row, field) => (field === 'Method' ? row.method : undefined),
              searchValues: (row) => [row.method],
            })
          }
          isEmpty={(rows) => rows.length === 0}
          emptyTitle="No payments in this window"
          skeleton={<TableSkeleton columns={2} />}
        >
          {(rows) => (
            <DataTable
              caption="Sales by payment method"
              layout={paymentMethodLayout}
              columns={paymentMethodColumns}
              rows={rows}
              rowKey={(row, index) => `${row.method}-${index}`}
              totals={{ label: 'Total', cells: sumTotals(rows, ['amount']) }}
            />
          )}
        </AsyncSection>
      </section>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-slate-900">By document type</h2>
        <ReportToolbar>
          <ReportSearchField
            value={docType.search}
            onChange={docType.setSearch}
            placeholder="Search document type"
          />
          <ReportFiltersButton
            fields={DOC_TYPE_FILTER_FIELDS}
            applied={docType.filterRows}
            matchAny={docType.matchAny}
            onApply={docType.applyFilters}
            placeholderFor={() => 'pos / invoice'}
          />
          <ReportColumnsButton layout={docTypeLayout} labels={DOC_TYPE_LABELS} />
        </ReportToolbar>
        <AsyncSection
          state={byDocTypeQuery}
          select={(rows: SalesByDocTypeRow[]) =>
            applyClientFilters(rows, {
              search: docType.search,
              filterRows: docType.filterRows,
              matchAny: docType.matchAny,
              fieldValue: (row, field) => (field === 'Document type' ? row.docType : undefined),
              searchValues: (row) => [row.docType],
            })
          }
          isEmpty={(rows) => rows.length === 0}
          emptyTitle="No sales in this window"
          skeleton={<TableSkeleton columns={3} />}
        >
          {(rows) => (
            <DataTable
              caption="Sales by document type"
              layout={docTypeLayout}
              columns={docTypeColumns}
              rows={rows}
              rowKey={(row, index) => `${row.docType}-${index}`}
              totals={{ label: 'Total', cells: sumTotals(rows, ['orders', 'gross']) }}
            />
          )}
        </AsyncSection>
      </section>
    </div>
  );
}
